// IT 監控 › 主機（`kmgr-hosts` `/kmgr/hosts`，SPEC #kinio-kms-ui 導覽表）：全部｜報到中｜未授權。
// 欄位：名稱（主要使用者小字）、家戶（跨組織「組織 › 家戶」）、類別、狀態（含已關機 / 睡眠中 / 更新中 / 不在現場 / 本機已暫停）、
// CPU / 記憶體 / 磁碟（只在異常時著色）、管理程式版本、金鑰保護。預期離線不是離線、不開事故（KMS-26）；未授權只回報在線與基本資訊（#kinio-kms-license）。
// 主機詳細 `/kmgr/hosts/:home/:target`：概況（關鍵主機、預期離線時段 PUT …/targets/{target}）、「回報了什麼」（遙測白名單，#kinio-kms-telemetry）。
// 資料：GET /v1/me/kmgr/hosts（跨組織一次請求）；席次 GET /v1/orgs/{org}/kmgr/seats、改派 PUT …/seats/{seat}。
// kmgr 0.11.0 還不帶主要使用者、CPU / 記憶體 / 磁碟、管理程式版本、金鑰保護、本機已暫停：有就畫、沒有寫「—」。
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApp } from '../../lib/AppContext';
import { useScope } from '../../lib/scope';
import { usePageSubject } from '../../lib/pageTitle';
import { stamp } from '../../lib/timefmt';
import { SkeletonLines } from '../../components/Skeleton';
import { Dialog } from '../../components/Dialog';
import { kmgrApi } from './api';
import { kmgrAdminOrgs, useHomeApi, useKmgrDashboard, useKmgrEnv } from './hooks';
import { hostHasIssue, hostMatches, primaryUserBody, isExpectedOffline, kmgrScopeOf, kmgrScopes, metricLevel, parseOrgFilter, TELEMETRY_NEVER, TELEMETRY_WHITELIST, validateWindows, type HostsTab } from './model';
import { orgApi, type MyOrg } from '../../lib/planeApi';
import { brandName, Crumbs, errText, LiveStamp, NS, osText, StateBox } from './parts';
import { tokenProgressHref } from './DeployTokens';
import { memberName, useOrgMembers } from './Oncall';
import { HostStatePill, SettingsEntry, UnlicensedMark, WindowsEditor } from './widgets';
import type { HostRow, OrgSeats, Seat, TimeWindow, UnlicensedHost } from './types';
import { HostRemoteButton } from './remote/HostRemoteButton';
import { OrgFilter } from './OrgFilter';

export function HostsPage() {
  const { homeId, targetRef } = useParams();
  useKmgrEnv();
  if (homeId && targetRef) return <HostDetail homeId={homeId} targetRef={decodeURIComponent(targetRef)} />;
  return <HostsList />;
}

const TABS: HostsTab[] = ['all', 'enrolling', 'unlicensed'];

function HostsList() {
  const { t } = useTranslation(NS);
  const { planes, scope, orgs } = useScope();
  const [sp, setSp] = useSearchParams();
  const tab = (TABS.includes(sp.get('tab') as HostsTab) ? sp.get('tab') : 'all') as HostsTab;
  const state = sp.get('state') ?? '';
  const cls = sp.get('class') ?? '';
  const issue = sp.get('issue') === '1';
  const search = sp.get('q') ?? '';
  const ks = kmgrScopeOf(scope);
  // 範圍「全部」時可再篩組織（多選 = 多個 org:<id>；M5-04）
  const orgF = ks.scope === 'all' ? parseOrgFilter(sp.get('org')) : [];
  const api = planes[0]?.api ?? '';
  const dash = useKmgrDashboard();
  const q = useInfiniteQuery({
    queryKey: ['kmgr', 'hosts', api, ks.scope, orgF.join(','), tab === 'unlicensed' ? 'unlicensed' : state],
    queryFn: ({ pageParam }) => kmgrApi.hosts(api, { scope: kmgrScopes(ks.scope, orgF), state: tab === 'unlicensed' ? 'unlicensed' : state || undefined, cursor: pageParam || undefined, limit: 100 }),
    initialPageParam: '',
    getNextPageParam: (last) => last?.next_cursor || undefined,
    enabled: !!api,
    refetchInterval: 15_000,
  });
  const counts = q.data?.pages[0]?.counts ?? {};
  let rows = (q.data?.pages ?? []).flatMap((p) => p?.hosts ?? []);
  if (cls) rows = rows.filter((h) => h.seat_class === cls);
  if (issue) rows = rows.filter((h) => hostHasIssue(h));
  if (search.trim()) rows = rows.filter((h) => hostMatches(h, search));
  const crossOrg = new Set(rows.map((h) => h.org_id)).size > 1 || orgs.filter((o) => o.kind === 'team').length > 1;
  const set = (k: string, v: string | null) => {
    const n = new URLSearchParams(sp);
    if (v === null || v === '') n.delete(k);
    else n.set(k, v);
    setSp(n, { replace: true });
  };
  const admins = kmgrAdminOrgs(orgs).filter((o) => o.kind === 'team' && (ks.scope === 'all' || ks.scope === 'org:' + o.org_id || dash.data?.homes.items.some((h) => h.org_id === o.org_id)));
  const seatQs = useQueries({
    queries: admins.map((o) => ({ queryKey: ['kmgr', 'seats', o.org_id], queryFn: () => kmgrApi.seats(o.api, o.org_id), enabled: tab === 'unlicensed', staleTime: 15_000 })),
  });
  const unlicensedN = counts.unlicensed ?? dash.data?.hosts.unlicensed;
  return (
    <section className="page km" data-testid="km-hosts-page">
      <Crumbs items={[{ label: t('nav.group'), to: '/kmgr/incidents' }, { label: t('nav.hosts') }]} />
      <div className="km-head">
        <h1 className="page-title">{t('nav.hosts')}</h1>
        <span className="spacer" />
        <LiveStamp updatedAt={q.dataUpdatedAt} stale={q.isError && !!q.data} />
        <SettingsEntry />
      </div>
      <nav className="km-tabs" aria-label={t('hosts.tabs')}>
        <Link to="/kmgr/hosts" aria-current={tab === 'all' ? 'page' : undefined}>
          {counts.all === undefined ? t('hosts.tab_all') : t('hosts.tab_all_n', { n: counts.all })}
        </Link>
        <Link to="/kmgr/hosts?tab=enrolling" aria-current={tab === 'enrolling' ? 'page' : undefined}>
          {t('hosts.tab_enrolling')}
        </Link>
        <Link to="/kmgr/hosts?tab=unlicensed" aria-current={tab === 'unlicensed' ? 'page' : undefined} data-testid="km-hosts-tab-unlicensed">
          {unlicensedN === undefined ? t('hosts.tab_unlicensed') : t('hosts.tab_unlicensed_n', { n: unlicensedN })}
        </Link>
      </nav>
      {tab === 'enrolling' ? (
        <Enrolling />
      ) : tab === 'unlicensed' ? (
        <Unlicensed orgs={admins} seats={seatQs.map((x) => x.data)} loading={seatQs.some((x) => x.isLoading)} rows={rows} rowsLoading={q.isLoading} crossOrg={crossOrg} />
      ) : (
        <>
          <div className="km-block-head" style={{ marginBottom: 8 }}>
            <label className="field" style={{ margin: 0 }}>
              <span className="km-sr">{t('hosts.filter_state')}</span>
              <select className="input" value={state} onChange={(e) => set('state', e.target.value)} aria-label={t('hosts.filter_state')}>
                <option value="">{t('hosts.state_any')}</option>
                {['online', 'offline', 'expected_offline', 'suspended', 'revoked'].map((s) => (
                  <option key={s} value={s}>
                    {t(`hosts.state_${s}`)}
                    {counts[s] !== undefined ? `（${counts[s]}）` : ''}
                  </option>
                ))}
              </select>
            </label>
            <label className="field" style={{ margin: 0 }}>
              <span className="km-sr">{t('hosts.filter_class')}</span>
              <select className="input" value={cls} onChange={(e) => set('class', e.target.value)} aria-label={t('hosts.filter_class')}>
                <option value="">{t('hosts.class_any')}</option>
                <option value="server">{t('seat.server')}</option>
                <option value="workstation">{t('seat.workstation')}</option>
              </select>
            </label>
            <button type="button" className={'km-chip' + (issue ? ' on' : '')} aria-pressed={issue} onClick={() => set('issue', issue ? null : '1')}>
              {issue ? '✓ ' : ''}
              {t('hosts.only_issues')}
            </button>
            <OrgFilter value={orgF} onChange={(v) => set('org', v.length ? v.join(',') : null)} />
            <label className="field" style={{ margin: 0 }}>
              <span className="km-sr">{t('hosts.search')}</span>
              <input className="input" type="search" value={search} placeholder={t('hosts.search')} aria-label={t('hosts.search')} onChange={(e) => set('q', e.target.value)} data-testid="km-hosts-search" />
            </label>
          </div>
          {q.isLoading ? (
            <div className="card">
              <SkeletonLines n={5} label={t('common.loading')} />
            </div>
          ) : q.isError && !q.data ? (
            <StateBox icon="⊘" title={t('hosts.unavailable')} body={errText(t, q.error)} testId="km-hosts-error">
              <button className="btn" onClick={() => void q.refetch()}>
                {t('common.retry')}
              </button>
            </StateBox>
          ) : rows.length === 0 ? (
            <StateBox
              icon="○"
              title={search.trim() && !state && !cls && !issue ? t('hosts.search_none', { q: search.trim() }) : state || cls || issue || search.trim() ? t('hosts.none_filtered') : t('hosts.none')}
              body={state || cls || issue || search.trim() ? undefined : t('hosts.none_body')}
              testId="km-hosts-empty"
            >
              {state || cls || issue || search.trim() ? (
                <Link className="btn" to="/kmgr/hosts">
                  {t('incidents.clear_filter')}
                </Link>
              ) : null}
            </StateBox>
          ) : (
            <div className="card">
              <HostsTable rows={rows} crossOrg={crossOrg} />
              <p className="muted km-sub" style={{ marginBottom: 0 }}>
                {q.hasNextPage ? (
                  <button className="btn btn-sm" disabled={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
                    {t('incidents.load_more')}
                  </button>
                ) : (
                  t('hosts.shown_all', { n: rows.length })
                )}
                {' · '}
                {t('hosts.footnote')}
              </p>
            </div>
          )}
        </>
      )}
    </section>
  );
}

export function hostHref(h: Pick<HostRow, 'home_id' | 'target_ref'>): string {
  return `/kmgr/hosts/${encodeURIComponent(h.home_id)}/${encodeURIComponent(h.target_ref)}`;
}

/** CPU · 記憶體 · 磁碟 一格：數字只在異常時著色；沒有指標寫「—」。 */
export function Metrics({ h }: { h: HostRow }) {
  const { t } = useTranslation(NS);
  const m = h.metrics;
  if (!m || (m.cpu_pct === undefined && m.mem_pct === undefined && !m.disks?.length)) return <span className="muted">—</span>;
  const cell = (kind: 'cpu' | 'mem' | 'disk', v: number | undefined, label?: string) => {
    const lv = metricLevel(kind, v);
    return (
      <span className={'km-metric ' + lv} data-metric={kind} data-level={lv} title={t(`host.metric_${kind}`)}>
        {label ? label + ' ' : ''}
        {v === undefined ? '—' : `${Math.round(v)}%`}
      </span>
    );
  };
  const disks = m.disks ?? [];
  const worst = disks.length ? disks.reduce((a, b) => (b.used_pct > a.used_pct ? b : a)) : undefined;
  return (
    <span className="tabular">
      {cell('cpu', m.cpu_pct)} · {cell('mem', m.mem_pct)} · {worst ? cell('disk', worst.used_pct, disks.length > 1 || /^[A-Z]:/.test(worst.mount) ? worst.mount : undefined) : <span className="km-metric none">—</span>}
    </span>
  );
}

function HostsTable({ rows, crossOrg }: { rows: HostRow[]; crossOrg: boolean }) {
  const { t } = useTranslation(NS);
  return (
    <table className="km-table" data-testid="km-hosts">
      <thead>
        <tr>
          <th>{t('hosts.col_name')}</th>
          <th>{crossOrg ? t('hosts.col_org_home') : t('hosts.col_home')}</th>
          <th>{t('hosts.col_class')}</th>
          <th>{t('hosts.col_state')}</th>
          <th>{t('hosts.col_metrics')}</th>
          <th>{t('hosts.col_version')}</th>
          <th>{t('hosts.col_key')}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((h) => (
          <tr key={h.target_ref} data-host={h.target_ref} data-expected={isExpectedOffline(h) ? '1' : undefined}>
            <td>
              <Link to={hostHref(h)}>
                <strong>{h.hostname || t('target.host')}</strong>
              </Link>
              {(h.primary_user || h.collector || h.critical) && (
                <span className="sub">{[h.primary_user, h.collector ? t('host.is_collector') : '', h.critical ? t('host.critical') : ''].filter(Boolean).join(' · ')}</span>
              )}
            </td>
            <td>{crossOrg && h.org_name ? `${h.org_name} › ${h.home_name ?? ''}` : (h.home_name ?? '')}</td>
            <td>{h.seat_class ? t(`seat.${h.seat_class}`) : '—'}</td>
            <td>
              <HostStatePill h={h} />
              {h.license_state === 'unlicensed' && (
                <>
                  {' '}
                  <UnlicensedMark />
                </>
              )}
              {h.local_paused && <span className="sub">{t('host.local_paused', { by: h.local_paused.by ?? '', time: h.local_paused.at ? stamp(h.local_paused.at) : '' })}</span>}
              {h.unexpected && <span className="sub">{t('token.unexpected')}</span>}
            </td>
            <td>
              <Metrics h={h} />
            </td>
            <td>{h.agent_version ?? '—'}</td>
            <td>{h.key_protection === 'hardware' ? t('host.key_hw') : h.key_protection === 'software' ? <span className="km-metric attention">{t('host.key_sw')}</span> : '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** 報到中：範圍內家戶還在用的佈署權杖（還有台數沒報到）→ 連到報到進度。 */
function Enrolling() {
  const { t } = useTranslation(NS);
  const dash = useKmgrDashboard();
  const { homes, planes } = useScope();
  const items = dash.data?.homes.items ?? [];
  const qs = useQueries({
    queries: items.map((h) => ({
      queryKey: ['kmgr', 'tokens', h.home_id],
      queryFn: () => kmgrApi.deployTokens(homes.find((x) => x.home_id === h.home_id)?.endpoints?.api ?? planes[0]?.api ?? '', h.home_id),
      staleTime: 15_000,
    })),
  });
  if (dash.loading || qs.some((q) => q.isLoading)) return <SkeletonLines n={3} label={t('common.loading')} />;
  const rows = qs.flatMap((q, i) => (q.data?.deploy_tokens ?? []).filter((tok) => tok.state === 'active' && (tok.progress?.remaining ?? tok.max_count - tok.used_count) > 0).map((tok) => ({ tok, home: items[i] })));
  if (!rows.length)
    return (
      <StateBox icon="○" title={t('hosts.enrolling_none')} body={t('hosts.enrolling_none_body')} testId="km-enrolling-empty">
        {items[0] && (
          <Link className="btn" to={`/sites/kmgr/${encodeURIComponent(items[0].home_id)}/tokens/new`}>
            {t('token.new')}
          </Link>
        )}
      </StateBox>
    );
  return (
    <div className="card">
      <table className="km-table" data-testid="km-enrolling">
        <thead>
          <tr>
            <th>{t('token.name')}</th>
            <th>{t('hosts.col_home')}</th>
            <th>{t('token.col_progress')}</th>
            <th>{t('token.col_expires')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ tok, home }) => (
            <tr key={tok.token_id}>
              <td>
                <Link to={tokenProgressHref(home.home_id, tok.token_id)}>{tok.name}</Link>
              </td>
              <td>{home.home_name}</td>
              <td>{t('token.progress_short', { a: tok.progress?.approved ?? tok.used_count, n: tok.max_count, f: tok.progress?.failed ?? 0 })}</td>
              <td>{stamp(tok.expires_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** 未授權：每個組織一段（席次摘要、未授權的電腦與〔改派授權…〕、〔加購〕或「請〈角色〉處理」）；不是組織管理員的只看清單。 */
function Unlicensed({ orgs, seats, loading, rows, rowsLoading, crossOrg }: { orgs: MyOrg[]; seats: (OrgSeats | undefined)[]; loading: boolean; rows: HostRow[]; rowsLoading: boolean; crossOrg: boolean }) {
  const { t } = useTranslation(NS);
  if (loading || rowsLoading) return <SkeletonLines n={4} label={t('common.loading')} />;
  const total = seats.reduce((a, s) => a + (s?.unlicensed.length ?? 0), 0) || rows.length;
  if (!total) return <StateBox icon="✓" title={t('hosts.unlicensed_none')} body={t('hosts.unlicensed_none_body')} testId="km-unlicensed-empty" />;
  return (
    <div data-testid="km-unlicensed">
      <div className="km-bar warn" role="status">
        <span className="ic" aria-hidden>
          ⚠
        </span>
        <span className="txt">
          <strong>{t('hosts.unlicensed_bar', { n: total })}</strong> {t('hosts.unlicensed_body')}
        </span>
      </div>
      {orgs.length
        ? orgs.map((o, i) => seats[i] && <OrgUnlicensed key={o.org_id} org={o} seats={seats[i]!} showOrg={crossOrg} />)
        : rows.length > 0 && (
            <div className="card">
              <HostsTable rows={rows} crossOrg={crossOrg} />
              <p className="muted km-sub">{t('hosts.unlicensed_need_admin')}</p>
            </div>
          )}
    </div>
  );
}

function OrgUnlicensed({ org, seats, showOrg }: { org: MyOrg; seats: OrgSeats; showOrg: boolean }) {
  const { t } = useTranslation(NS);
  const [picking, setPicking] = useState<UnlicensedHost | null>(null);
  // 〔加購〕只給有 org.billing 的人；其他人寫「請〈擁有者名〉處理」
  const members = useOrgMembers(org);
  const ownerName = memberName(members.data, members.data?.find((m) => m.role === 'owner')?.user_id);
  return (
    <section className="card km" data-org={org.org_id} data-testid="km-unlicensed-org">
      {showOrg && <h2 style={{ marginTop: 0, fontSize: 16 }}>{org.display_name}</h2>}
      {seats.unlicensed.length > 0 && (
        <table className="km-table">
          <thead>
            <tr>
              <th>{t('hosts.col_name')}</th>
              <th>{t('hosts.col_home')}</th>
              <th>{t('hosts.col_class')}</th>
              <th>{t('hosts.col_since')}</th>
              <th>{t('hosts.col_actions')}</th>
            </tr>
          </thead>
          <tbody>
            {seats.unlicensed.map((u) => (
              <tr key={u.device_ref} data-host={u.device_ref}>
                <td>
                  <strong>{u.hostname || t('target.host')}</strong>
                  {u.reason === 'released' && <span className="sub">{t('hosts.reason_released')}</span>}
                </td>
                <td>{u.home_name ?? ''}</td>
                <td>{u.seat_class ? t(`seat.${u.seat_class}`) : '—'}</td>
                <td>{u.since ? stamp(u.since) : '—'}</td>
                <td>
                  <button type="button" className="btn btn-sm" onClick={() => setPicking(u)}>
                    {t('seat.reassign_btn')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <h3 className="km-todo-title">{t('seat.summary_title')}</h3>
      <div className="km-seat-bars">
        {seats.summary.map((s) => (
          <div key={s.class} className="card" data-seat-class={s.class}>
            <div className="muted km-sub">{t(`seat.${s.class}`)}</div>
            <div className="v">
              {s.assigned} / {s.total}
            </div>
            <div className="km-sub muted">{s.available ? t('seat.available_n', { n: s.available }) : t('seat.all_used')}</div>
          </div>
        ))}
      </div>
      <p className="km-sub" style={{ marginTop: 12 }}>
        {seats.can_buy ? (
          <Link className="btn btn-primary btn-sm" to="/billing">
            {t('seat.buy')}
          </Link>
        ) : (
          <span className="muted">{t('seat.need_billing', { name: ownerName || t('seat.owner_role') })}</span>
        )}{' '}
        <span className="muted">{t('seat.reassign_hint')}</span>
      </p>
      {picking && <ReassignDialog org={org} seats={seats} target={picking} onClose={() => setPicking(null)} />}
    </section>
  );
}

/** 改派授權：選一席（同類別、已指派給別台或空席）給這台；PUT …/seats/{seat} {assigned_ref}（原持有者轉未授權）。 */
function ReassignDialog({ org, seats, target, onClose }: { org: { org_id: string; api: string }; seats: OrgSeats; target: UnlicensedHost; onClose(): void }) {
  const { t } = useTranslation(NS);
  const qc = useQueryClient();
  const cands = seats.seats.filter((s) => !target.seat_class || s.class === target.seat_class);
  const [seat, setSeat] = useState<string>(cands.find((s) => !s.assigned_ref)?.seat_id ?? cands[0]?.seat_id ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const chosen = cands.find((s) => s.seat_id === seat);
  const run = async () => {
    if (!chosen) return;
    setBusy(true);
    setErr(null);
    try {
      await kmgrApi.putSeat(org.api, org.org_id, chosen.seat_id, { assigned_ref: target.device_ref });
      await qc.invalidateQueries({ queryKey: ['kmgr'] });
      onClose();
    } catch (e) {
      setErr(errText(t, e));
    } finally {
      setBusy(false);
    }
  };
  const seatLabel = (s: Seat) => (s.assigned_ref ? t('seat.option_assigned', { host: s.hostname || s.assigned_ref, time: s.assigned_at ? stamp(s.assigned_at) : '' }) : t('seat.option_free'));
  return (
    <Dialog open title={t('seat.reassign_title', { host: target.hostname || target.device_ref })} onClose={onClose} testId="km-reassign">
      <p className="muted">{t('seat.reassign_body')}</p>
      {cands.length ? (
        <label className="field">
          <span>{t('seat.pick')}</span>
          <select className="input" value={seat} onChange={(e) => setSeat(e.target.value)}>
            {cands.map((s) => (
              <option key={s.seat_id} value={s.seat_id}>
                {seatLabel(s)}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <p className="error">{t('seat.no_seats')}</p>
      )}
      {chosen?.assigned_ref && <p className="km-bar warn">{t('seat.reassign_warn', { host: chosen.hostname || chosen.assigned_ref })}</p>}
      {err && (
        <p className="error" role="alert">
          {err}
        </p>
      )}
      <div className="dialog-actions">
        <button className="btn" onClick={onClose}>
          {t('common.cancel')}
        </button>
        <button className="btn btn-primary" disabled={!chosen || busy} onClick={() => void run()}>
          {t('seat.reassign_ok')}
        </button>
      </div>
    </Dialog>
  );
}

// ---- 主機詳細 ----

function HostDetail({ homeId, targetRef }: { homeId: string; targetRef: string }) {
  const { t } = useTranslation(NS);
  const { config } = useApp();
  const { api, home } = useHomeApi(homeId);
  const { orgs } = useScope();
  const [sp] = useSearchParams();
  const tab = sp.get('tab') === 'telemetry' ? 'telemetry' : 'overview';
  const q = useQuery({ queryKey: ['kmgr', 'hosts', api, 'home:' + homeId, ''], queryFn: () => kmgrApi.hosts(api ?? '', { scope: 'home:' + homeId, limit: 200 }), enabled: !!api, refetchInterval: 15_000 });
  const h = q.data?.hosts.find((x) => x.target_ref === targetRef);
  usePageSubject(h?.hostname);
  const org = orgs.find((o) => o.org_id === home?.org_id);
  const crumbs = [{ label: t('nav.group'), to: '/kmgr/incidents' }, { label: t('nav.hosts'), to: '/kmgr/hosts' }, ...(org ? [{ label: org.display_name }] : []), { label: home?.display_name ?? homeId }, { label: h?.hostname ?? t('target.host') }];
  if (q.isLoading)
    return (
      <section className="page km">
        <Crumbs items={crumbs} />
        <div className="card">
          <SkeletonLines n={5} label={t('common.loading')} />
        </div>
      </section>
    );
  if (!h)
    return (
      <section className="page km" data-testid="km-host-missing">
        <Crumbs items={crumbs} />
        <h1 className="page-title">{t('nav.hosts')}</h1>
        <StateBox icon="⊘" title={t('host.not_found')} body={q.isError ? errText(t, q.error) : t('host.not_found_body')}>
          <Link className="btn" to="/kmgr/hosts">
            {t('host.back_list')}
          </Link>
        </StateBox>
      </section>
    );
  const base = hostHref(h);
  return (
    <section className="page km" data-testid="km-host" data-host={h.target_ref}>
      <Crumbs items={crumbs} />
      <div className="km-head">
        <div>
          <h1 className="page-title" style={{ display: 'inline' }}>
            {h.hostname || t('target.host')}
          </h1>{' '}
          <HostStatePill h={h} /> {h.license_state === 'unlicensed' && <UnlicensedMark />}
          {h.primary_user && <div className="muted km-sub">{t('host.primary_user', { name: h.primary_user })}</div>}
        </div>
        <span className="spacer" />
        <LiveStamp updatedAt={q.dataUpdatedAt} stale={q.isError && !!q.data} />
        <Link className="btn btn-sm" to={`/sites/kmgr/${encodeURIComponent(homeId)}`}>
          {t('home_settings.title')}
        </Link>
        {/* 遠端協助（M4-06）：沒有這台的權限不畫 */}
        <HostRemoteButton h={h} api={api ?? ''} />
      </div>
      {h.license_state === 'unlicensed' && (
        <div className="km-bar warn" role="status">
          <span className="ic" aria-hidden>
            ⚠
          </span>
          <span className="txt">
            {t('hosts.unlicensed_body')}{' '}
            <Link to="/kmgr/hosts?tab=unlicensed">{t('hosts.tab_unlicensed')}</Link>
          </span>
        </div>
      )}
      {h.local_paused && (
        <div className="km-bar unk" role="status">
          <span className="ic" aria-hidden>
            ‖
          </span>
          <span className="txt">{t('host.local_paused_bar', { by: h.local_paused.by ?? '', time: h.local_paused.at ? stamp(h.local_paused.at) : '' })}</span>
        </div>
      )}
      <nav className="km-tabs" aria-label={t('host.tabs')}>
        <Link to={base} aria-current={tab === 'overview' ? 'page' : undefined}>
          {t('host.tab_overview')}
        </Link>
        <Link to={base + '?tab=telemetry'} aria-current={tab === 'telemetry' ? 'page' : undefined}>
          {t('host.tab_telemetry')}
        </Link>
      </nav>
      {tab === 'telemetry' ? <Telemetry h={h} brand={brandName(config.display_name)} orgName={org?.display_name} /> : <Overview h={h} api={api ?? ''} homeName={home?.display_name ?? ''} />}
    </section>
  );
}

function Overview({ h, api, homeName }: { h: HostRow; api: string; homeName: string }) {
  const { t } = useTranslation(NS);
  const m = h.metrics;
  return (
    <>
      {m && (
        <div className="km-kpis" data-testid="km-host-kpis">
          <Kpi label={t('host.metric_cpu')} v={m.cpu_pct} kind="cpu" />
          <Kpi label={t('host.metric_mem')} v={m.mem_pct} kind="mem" />
          {(m.disks ?? []).map((d) => (
            <Kpi key={d.mount} label={t('host.disk_mount', { mount: d.mount })} v={d.used_pct} kind="disk" />
          ))}
        </div>
      )}
      <section className="card">
        <h2 style={{ marginTop: 0, fontSize: 16 }}>{t('host.this_pc')}</h2>
        <dl className="km-dl">
          <dt>{t('hosts.col_class')}</dt>
          <dd>{h.seat_class ? t('host.class_seat', { cls: t(`seat.${h.seat_class}`) }) : '—'}</dd>
          <dt>{t('hosts.col_home')}</dt>
          <dd>{homeName}</dd>
          <dt>{t('token.col_os')}</dt>
          <dd>{osText(h.os) || '—'}</dd>
          <dt>{t('host.last_seen')}</dt>
          <dd>{h.last_seen_at ? stamp(h.last_seen_at) : '—'}</dd>
          <dt>{t('hosts.col_version')}</dt>
          <dd>{h.agent_version ?? '—'}</dd>
          <dt>{t('hosts.col_key')}</dt>
          <dd>{h.key_protection === 'hardware' ? t('host.key_hw') : h.key_protection === 'software' ? t('host.key_sw') : '—'}</dd>
          {h.collector && (
            <>
              <dt>{t('host.collector_scan')}</dt>
              <dd>
                {h.collector.assignments.length ? h.collector.assignments.join('、') : t('host.collector_none')}
                {h.collector.last_error && <span className="sub km-metric attention">{t('host.collector_error', { code: h.collector.last_error })}</span>}
              </dd>
            </>
          )}
        </dl>
      </section>
      <CriticalAndOffline h={h} api={api} />
    </>
  );
}

function Kpi({ label, v, kind }: { label: string; v: number | undefined; kind: 'cpu' | 'mem' | 'disk' }) {
  const lv = metricLevel(kind, v);
  return (
    <div className="card" data-level={lv}>
      <div className="muted km-sub">{label}</div>
      <div className={'v km-metric ' + lv}>{v === undefined ? '—' : `${Math.round(v)}%`}</div>
    </div>
  );
}

/** 關鍵主機（離線與服務停止 P1、防抖 3 分鐘）、主要使用者與預期離線時段：PUT …/targets/{target}（kmgr.configure）。 */
function CriticalAndOffline({ h, api }: { h: HostRow; api: string }) {
  const { t } = useTranslation(NS);
  const qc = useQueryClient();
  const { orgs } = useScope();
  const org = orgs.find((o) => o.org_id === h.org_id);
  // 組織成員（給主要使用者挑；讀不到就只能打字）
  const members = useQuery({ queryKey: ['org-members', org?.org_id], queryFn: () => orgApi.members(org!), enabled: !!org, staleTime: 60_000, retry: false });
  const activeMembers = (members.data ?? []).filter((m) => m.status === 'active' && m.display_name);
  const [user, setUser] = useState<string | undefined>(undefined);
  const userText = user ?? h.primary_user ?? '';
  const userDirty = userText.trim().replace(/\s+/g, ' ') !== (h.primary_user ?? '');
  const [critical, setCritical] = useState<boolean | undefined>(undefined);
  const [windows, setWindows] = useState<TimeWindow[] | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const crit = critical ?? !!h.critical;
  const ws = windows ?? h.expected_offline?.windows ?? [];
  const tz = h.expected_offline?.tz || Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Taipei';
  const dirty = userDirty || crit !== !!h.critical || JSON.stringify(ws) !== JSON.stringify(h.expected_offline?.windows ?? []);
  const errs = validateWindows(ws);
  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await kmgrApi.putTarget(api, h.home_id, h.target_ref, {
        ...(crit !== !!h.critical ? { critical: crit } : {}),
        ...(userDirty ? primaryUserBody(userText, activeMembers) : {}),
        ...(JSON.stringify(ws) !== JSON.stringify(h.expected_offline?.windows ?? []) ? { expected_offline: ws.length ? { tz, windows: ws } : null } : {}),
      });
      await qc.invalidateQueries({ queryKey: ['kmgr', 'hosts'] });
      setUser(undefined);
      setCritical(undefined);
      setWindows(undefined);
      setMsg({ ok: true, text: t('host.saved') });
    } catch (e) {
      setMsg({ ok: false, text: errText(t, e) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="card" data-testid="km-host-settings">
      <h2 style={{ marginTop: 0, fontSize: 16 }}>{t('host.settings_title')}</h2>
      <label className="km-check">
        <input type="checkbox" checked={crit} onChange={(e) => setCritical(e.target.checked)} />
        <span>
          <strong>{t('host.critical')}</strong>
          <span className="sub muted km-sub" style={{ display: 'block' }}>
            {t('host.critical_hint')}
          </span>
        </span>
      </label>
      <label className="field" style={{ marginTop: 12 }}>
        <span>
          <strong>{t('host.primary_user_label')}</strong>
        </span>
        <input className="input" value={userText} maxLength={64} placeholder={t('host.primary_user_ph')} list={activeMembers.length ? 'km-primary-user-members' : undefined} onChange={(e) => setUser(e.target.value)} data-testid="km-host-primary-user" />
        <span className="sub muted km-sub">{t('host.primary_user_hint')}</span>
      </label>
      {activeMembers.length > 0 && (
        <datalist id="km-primary-user-members" aria-label={t('host.primary_user_member')}>
          {activeMembers.map((m) => (
            <option key={m.user_id} value={m.display_name} />
          ))}
        </datalist>
      )}
      <h3 className="km-todo-title">{t('host.expected_offline')}</h3>
      <p className="muted km-sub">{h.seat_class === 'workstation' ? t('host.expected_offline_ws') : t('host.expected_offline_hint')}</p>
      <WindowsEditor value={ws} onChange={setWindows} addDefault={{ days: [1, 2, 3, 4, 5], start: '19:00', end: '08:00' }} label={t('host.expected_offline')} />
      {errs.length > 0 && (
        <p className="error km-sub" role="alert">
          {errs.map((k) => t(k)).join('；')}
        </p>
      )}
      {msg && (
        <p className={msg.ok ? 'notice' : 'error'} role="status">
          {msg.text}
        </p>
      )}
      <div className="km-dock">
        <span className="why">{t('host.save_note')}</span>
        <button className="btn btn-primary" disabled={!dirty || busy || errs.length > 0} onClick={() => void save()}>
          {busy ? t('common.saving') : t('notify.save')}
        </button>
      </div>
    </section>
  );
}

/** 「回報了什麼」：規格的遙測白名單（#kinio-kms-telemetry）與一律不收的清單；要看實際的值在那台電腦打開管理程式。 */
function Telemetry({ h, brand, orgName }: { h: HostRow; brand: string; orgName?: string }) {
  const { t } = useTranslation(NS);
  const agent = t('agent_name', { brand });
  return (
    <section className="card km-tele" data-testid="km-host-telemetry">
      <p className="muted km-sub" style={{ marginTop: 0 }}>
        {t('telemetry.intro', { agent, org: orgName ?? '' })} {h.last_seen_at ? t('telemetry.last_upload', { time: stamp(h.last_seen_at) }) : ''}
      </p>
      <dl>
        {TELEMETRY_WHITELIST.map((k) => (
          <div key={k}>
            <dt>{t(`telemetry.item_${k}`)}</dt>
            <dd>{t(`telemetry.desc_${k}`)}</dd>
          </div>
        ))}
      </dl>
      <h3 className="km-todo-title">{t('telemetry.never_title')}</h3>
      <p>{TELEMETRY_NEVER.map((k) => t(`telemetry.never_${k}`)).join('、')}。</p>
      <p className="muted km-sub">{t('telemetry.local_page', { agent })}</p>
    </section>
  );
}
